import {
  type SnackbarErrorHandlerFactory,
  type SnackbarFactory,
  type SnackbarRetryWrapperFactory,
  type StartHandoffFn,
} from '@/features/shell'
import {
  type GetCurrentMemberFn,
  type GetCurrentUserFn,
  type GetCurrentWorkspaceFn,
  type GetUserFn,
  type PrimeUserFn,
  type PrimeWorkspaceFn,
} from '@/features/iam'
import { type Observable } from 'rxjs'
import { type Id } from '@/infra/id'
import { type JobGist } from '@/features/jobs/JobGist'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import type {
  DocumentLinkingViewModelFactory,
  DocumentsListViewModelFactory,
} from '@/features/docs'
import type {
  AddCouponFn,
  ApplePayViewModelFactory,
  BillingMethodListViewModelFactory,
  CardBillingMethodEditorViewModelFactory,
  CheckHasBillingMethodFn,
  FetchCouponFn,
  ListCouponsFn,
  NewBillingMethodDialogViewModelFactory,
  TransactionsTableViewModelFactory,
} from '@/features/billing'
import type { PublishedLegend } from '@/features/jobs/legends/PublishedLegend'
import type {
  ConversationViewModelFactory,
  UnreadCountFn,
} from '@/features/messages'
import type { ListSearchableJobsQuery } from './list/SearchableJobStore'
import type { CursorPaged } from '@/infra/paging'
import type { SearchableJob } from './list/SearchableJob'
import type LegendSettings from './legends/LegendSettings'
import type { JobProgression } from './taxes/progression/JobProgression'
import type { NavigateFn } from '@/RootStore'
import type { MessageCreated } from '@/features/messages/message-events'
import type { PageActivityDescriptor } from '@/infra/page-activity'
import type { BookkeepingPlatformSelectionDialogViewModelFactory } from '@/features/bookkeeping'
import type {
  BusinessProfileEditorViewModelFactory,
  DependentProfileEditorViewModelFactory,
  FetchBusinessProfileForJobFn,
  FetchBusinessTaxRefundMethodForJobFn,
  FetchDependentProfileVersionForJobFn,
  FetchPersonalProfileVersionForJobFn,
  FetchPersonalTaxRefundMethodForJobFn,
  FetchSpousalProfileVersionForJobFn,
  ListBusinessProfilesFn,
  ListDependentsFn,
  ListTaxRefundMethodsFn,
  PersonalProfileEditorViewModelFactory,
  PromptDeleteBusinessProfileFn,
  PromptDeleteDependentFn,
  PromptDeleteTaxRefundMethodFn,
  SpousalProfileEditorViewModelFactory,
  TaxRefundMethodEditorViewModelFactory,
} from '@/features/tax-profile'
import type {
  EnsureSection7216AcceptanceFn,
  EnsureTermsAcceptanceFn,
} from '@/features/account'
import type {
  CreateNewDraftJobFn,
  FetchLatestPublishedLegendVersionFn,
} from '@/features/jobs/internal'
import type {
  FetchQuestionsQuery,
  FetchQuestionsResult,
} from '@/features/jobs/questions/QuestionStore'
import type { SessionOptions } from '@/features/iam/auth/SessionOptions'
import type { ExitMethod } from '@/features/shell/exiting/ExitMethod'
import type {
  GetAddToCalendarDelegate,
  GetExternalUrlDelegate,
} from '@/features/shell/platform/types'

/**
 * Options for fetching a job.
 */
export interface FetchJobOptions {
  /**
   * Reloads the job if already cached.
   */
  force?: boolean
}

/**
 * Delegate for fetching a job gist.
 */
export type FetchJobGistFn = (
  id: Id,
  options?: FetchJobOptions,
) => Promise<JobGist | null>

/**
 * Options and dependencies needed by the jobs feature.
 */
export interface JobsFeatureOptions {
  token$: Observable<string>
  navigate: NavigateFn
  getCurrentMember: GetCurrentMemberFn
  getCurrentUser: GetCurrentUserFn
  getCurrentWorkspace: GetCurrentWorkspaceFn
  getUser: GetUserFn
  primeUser: PrimeUserFn
  primeWorkspace: PrimeWorkspaceFn
  pageExitMethod$: Observable<ExitMethod>
  newSnackbar: SnackbarFactory
  getAddToCalendarDelegate: GetAddToCalendarDelegate
  getExternalUrlDelegate: GetExternalUrlDelegate
  createSnackbarErrorHandler: SnackbarErrorHandlerFactory
  createSnackbarRetryWrapper: SnackbarRetryWrapperFactory
  sessionOptions$: Observable<SessionOptions>
  ensureTermsAcceptance: EnsureTermsAcceptanceFn
  ensureSection7216Accepted: EnsureSection7216AcceptanceFn
  startHandoff: StartHandoffFn
  documentsListViewModelFactory: PromiseLike<DocumentsListViewModelFactory>
  createTransactionTableViewModelFactory: PromiseLike<TransactionsTableViewModelFactory>
  newBillingMethodDialogViewModelFactory: PromiseLike<NewBillingMethodDialogViewModelFactory>
  createBillingMethodListViewModel: PromiseLike<BillingMethodListViewModelFactory>
  createCardBillingMethodEditorViewModel: PromiseLike<CardBillingMethodEditorViewModelFactory>
  createApplePayViewModel: PromiseLike<ApplePayViewModelFactory>
  checkHasBillingMethod: CheckHasBillingMethodFn
  listCoupons: ListCouponsFn
  addCoupon: AddCouponFn
  fetchCoupon: FetchCouponFn
  documentLinkingViewModelFactory: PromiseLike<DocumentLinkingViewModelFactory>
  createBookkeepingPlatformSelectionDialogViewModelFactory: PromiseLike<BookkeepingPlatformSelectionDialogViewModelFactory>
  createPersonalProfileEditorViewModel: PromiseLike<PersonalProfileEditorViewModelFactory>
  fetchPersonalProfileVersionForJob: FetchPersonalProfileVersionForJobFn
  createSpousalProfileEditorViewModel: PromiseLike<SpousalProfileEditorViewModelFactory>
  fetchSpousalProfileVersionForJob: FetchSpousalProfileVersionForJobFn
  createDependentProfileEditorViewModel: PromiseLike<DependentProfileEditorViewModelFactory>
  fetchDependentProfileVersionForJob: FetchDependentProfileVersionForJobFn
  listDependents: ListDependentsFn
  promptDeleteDependent: PromptDeleteDependentFn
  conversationViewModelFactory: PromiseLike<ConversationViewModelFactory>
  incomingMessages$: Observable<MessageCreated>
  unreadCount: UnreadCountFn
  pageActivity$: Observable<PageActivityDescriptor>
  userActive$: Observable<boolean>
  createTaxRefundMethodEditorViewModel: PromiseLike<TaxRefundMethodEditorViewModelFactory>
  fetchPersonalTaxRefundMethodForJob: FetchPersonalTaxRefundMethodForJobFn
  listTaxRefundMethods: ListTaxRefundMethodsFn
  promptDeleteTaxRefundMethod: PromptDeleteTaxRefundMethodFn
  createBusinessProfileEditorViewModel: PromiseLike<BusinessProfileEditorViewModelFactory>
  fetchBusinessProfileForJob: FetchBusinessProfileForJobFn
  listBusinessProfiles: ListBusinessProfilesFn
  promptDeleteBusinessProfile: PromptDeleteBusinessProfileFn
  fetchBusinessTaxRefundMethodForJob: FetchBusinessTaxRefundMethodForJobFn
}

/**
 * Options for {@link PromptArchiveJobFn}
 */
export interface PromptArchiveJobOptions {
  /**
   * ID of the job.
   */
  id: Id
  /**
   * Getter for the display name of the job.
   */
  displayName(): string
  /**
   * An abort signal.
   */
  signal?: AbortSignal
}

/**
 * Function to prompt and archive a job.
 * @returns A boolean promise that resolves to true if the job was archived, or false if cancelled.
 */
export type PromptArchiveJobFn = (
  options: PromptArchiveJobOptions,
) => Promise<boolean>

/**
 * Delegate for listing published legends.
 */
export type ListPublishedLegendsFn = (
  workspaceId: Id,
  signal?: AbortSignal,
) => Promise<PublishedLegend[]>

/**
 * Props used to prime a legend's settings in the store before it is fully loaded.
 */
export interface PrimeLegendSettingsProps {
  id: Id
  version: number
}

/**
 * Delegate used for priming a legend settings.
 */
export type PrimeLegendSettingsFn = (
  props: PrimeLegendSettingsProps,
) => LegendSettings

/**
 * Props used to prime a job progression in the store before it is fully loaded.
 */
export interface PrimeJobProgressionProps {
  id: Id
}

/**
 * Delegate used for priming a job progression.
 */
export type PrimeJobProgressionFn = (
  props: PrimeJobProgressionProps,
) => JobProgression

/**
 * Delegate for listing searchable jobs.
 */
export type ListSearchableJobsFn = (
  query: ListSearchableJobsQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<SearchableJob>>

/**
 * Delegate for fetching the current state of the questionnaire.
 */
export type FetchQuestionsFn = (
  query: FetchQuestionsQuery,
  signal?: AbortSignal,
) => Promise<FetchQuestionsResult>

/**
 * Features relating to jobs.
 */
export type JobsFeature = ReturnType<typeof JobsFeature>

export function JobsFeature(options: JobsFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (module) => module.InternalJobsFeature(options),
  )

  const fetchJobGist: FetchJobGistFn = internal.wrap(
    (m) => m.jobGistStore.fetchJobGist,
  )

  const jobGistRealtime$ = internal.defer((m) => m.jobGistStore.realtime$)
  const newSearchableJob$ = internal.defer(
    (m) => m.searchableJobStore.newSearchableJob$,
  )

  return {
    // Internal
    internal,

    // Public
    fetchJobGist,
    jobGistRealtime$,
    newSearchableJob$,
    refreshSearchableJobs$: internal.defer(
      (m) => m.searchableJobStore.refreshSearchableJobs$,
    ),
    systemNotifications$: internal.defer((m) => m.systemNotifications$),
    listPublishedLegends: internal.wrap(
      (m) => m.listPublishedLegends,
    ) satisfies ListPublishedLegendsFn,
    listSearchableJobs: internal.wrap(
      (m) => m.searchableJobStore.list,
    ) satisfies ListSearchableJobsFn,
    fetchLatestPublishedLegend: internal.wrap(
      (m) => m.fetchLatestPublishedLegendVersion,
    ) satisfies FetchLatestPublishedLegendVersionFn,
    createNewDraftJob: internal.wrap(
      (m) => m.createNewDraftJob,
    ) satisfies CreateNewDraftJobFn,
    promptArchiveJob: internal.wrap(
      (m) => m.promptArchiveJob,
    ) satisfies PromptArchiveJobFn,
    createJobListViewModel: internal.lazy((m) => m.createJobListViewModel),
  }
}
